import React, { useCallback, useEffect, useMemo, useState } from "react";
import { KeyRound, Users, LayoutGrid, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import * as api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { Button } from "../ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { UserSearchSelect } from "../ui/UserSearchSelect";
import { UserAvatar } from "../shared/UserAvatar";
import { EmptyState } from "../shared/EmptyState";
import { PermissionMatrix } from "../access/PermissionMatrix";
import { UserPermissionsDialog } from "../access/UserPermissionsDialog";
import { LEVEL_LABELS } from "../access/FeatureLevelControl";
import { ROLES } from "../../lib/utils";

function PermBadges({ perms, features }) {
    return (
        <div className="flex flex-wrap gap-1">
            {perms.map((p) => {
                const [key, lvl] = p.split(":");
                const f = features.find((x) => x.key === key);
                return (
                    <span key={p} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-teal-50 text-teal-700 border border-teal-100" data-testid={`access-badge-${p.replace(":", "-")}`}>
                        {f?.label || key}{f?.levels.length > 1 ? ` · ${LEVEL_LABELS[lvl]}` : ""}
                    </span>
                );
            })}
        </div>
    );
}

function UserAccessTab() {
    const { users, departments, permCatalog, hasPermission } = useApp();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [pickedId, setPickedId] = useState("");
    const [dialogUser, setDialogUser] = useState(null);

    const load = useCallback(async () => {
        try { setRows(await api.getUsersWithAccess()); }
        catch { toast.error("Erreur de chargement"); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const candidates = useMemo(() => users.filter((u) => u.role !== "admin" && u.is_active !== false && u.user_type !== "external"), [users]);

    const removeAll = async (u) => {
        if (!window.confirm(`Retirer toutes les fonctionnalités supplémentaires de ${u.first_name} ${u.last_name} ?`)) return;
        try { await api.setUserPermissions(u.id, []); toast.success("Accès retirés"); load(); }
        catch { toast.error("Erreur"); }
    };

    if (!hasPermission("admin")) return null;

    return (
        <div className="space-y-4">
            <div className="flex items-start gap-3 flex-wrap">
                <p className="text-sm text-gray-500 flex-1 min-w-[260px]">
                    Accordez des fonctionnalités à un utilisateur en plus de celles de son rôle — par exemple l'inventaire à un collaborateur sans en faire un Agent IT.
                </p>
                {adding ? (
                    <div className="flex items-center gap-2 w-full sm:w-[420px]" data-testid="access-add-panel">
                        <div className="flex-1">
                            <UserSearchSelect users={candidates} departments={departments} value={pickedId} onChange={(id) => { setPickedId(id); const u = users.find((x) => x.id === id); if (u) { setDialogUser(u); setAdding(false); setPickedId(""); } }} placeholder="Choisir un utilisateur..." data-testid="access-user-select" />
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setAdding(false); setPickedId(""); }} data-testid="access-add-cancel">Annuler</Button>
                    </div>
                ) : (
                    <Button onClick={() => setAdding(true)} data-testid="access-add-btn"><Plus className="w-4 h-4 mr-1" />Ajouter un accès</Button>
                )}
            </div>

            {loading ? (
                <p className="text-sm text-gray-400">Chargement...</p>
            ) : rows.length === 0 ? (
                <EmptyState icon={KeyRound} title="Aucun accès supplémentaire" description="Aucun utilisateur ne dispose encore de fonctionnalités au-delà de son rôle." />
            ) : (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm" data-testid="access-users-table">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-600">
                                <th className="px-4 py-2.5 font-semibold">Utilisateur</th>
                                <th className="px-4 py-2.5 font-semibold">Rôle</th>
                                <th className="px-4 py-2.5 font-semibold">Fonctionnalités supplémentaires</th>
                                <th className="px-4 py-2.5" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((u) => (
                                <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50/60" data-testid={`access-row-${u.id}`}>
                                    <td className="px-4 py-2.5">
                                        <div className="flex items-center gap-2.5">
                                            <UserAvatar user={u} size="sm" />
                                            <div className="min-w-0">
                                                <div className="font-medium text-gray-900 truncate">{u.first_name} {u.last_name}</div>
                                                <div className="text-[11px] text-gray-400 truncate">{u.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-2.5 text-gray-600">{ROLES.find((r) => r.id === u.role)?.label || u.role}</td>
                                    <td className="px-4 py-2.5"><PermBadges perms={u.permissions || []} features={permCatalog?.features || []} /></td>
                                    <td className="px-4 py-2.5">
                                        <div className="flex items-center justify-end gap-0.5">
                                            <Button variant="ghost" size="sm" onClick={() => setDialogUser(u)} title="Modifier" data-testid={`access-edit-${u.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                                            <Button variant="ghost" size="sm" onClick={() => removeAll(u)} title="Tout retirer" data-testid={`access-remove-${u.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <UserPermissionsDialog user={dialogUser} open={!!dialogUser} onClose={() => setDialogUser(null)} onSaved={load} />
        </div>
    );
}

export default function AdminAccess() {
    const { hasPermission } = useApp();
    if (!hasPermission("admin")) {
        return <div className="page-container"><EmptyState icon={KeyRound} title="Accès réservé aux administrateurs" /></div>;
    }
    return (
        <div className="page-container" data-testid="admin-access-page">
            <div className="flex items-center gap-3 mb-4">
                <KeyRound className="w-5 h-5 text-teal-600" />
                <h1 className="text-lg font-bold text-gray-900">Rôles & accès</h1>
            </div>
            <Tabs defaultValue="roles">
                <TabsList className="mb-4">
                    <TabsTrigger value="roles" data-testid="access-tab-roles"><LayoutGrid className="w-3.5 h-3.5 mr-1.5" />Plan des rôles</TabsTrigger>
                    <TabsTrigger value="users" data-testid="access-tab-users"><Users className="w-3.5 h-3.5 mr-1.5" />Accès par utilisateur</TabsTrigger>
                </TabsList>
                <TabsContent value="roles">
                    <p className="text-sm text-gray-500 mb-3">Fonctionnalités accordées par défaut à chaque rôle. L'Administrateur dispose toujours de tout ; les autres rôles sont modifiables.</p>
                    <PermissionMatrix />
                </TabsContent>
                <TabsContent value="users"><UserAccessTab /></TabsContent>
            </Tabs>
        </div>
    );
}
